import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDetailResponseSchema,
  AlertListResponseSchema,
  AlertSchema,
  type AlertStatus,
  BulkAckResponseSchema,
  NoisiestRulesResponseSchema,
} from '@vision360/contracts';
import { useApi } from '@/lib/auth/session-context';
import { alertKeys } from '@/widgets/_sdk/hooks';

/** Alert lifecycle over the API (TechSpec §6.1, §12; PRD VS-061…063). */

export const PAGE_SIZE = 50;
/** The API's ceiling for one bulk acknowledgement. */
const BULK_MAX = 200;

/**
 * The alert list, newest first, one page per "Charger plus" (`before` = the
 * last openedAt). Infinite rather than `useAlerts` so the pages form one list
 * for the bulk selection; the key stays under `['alerts']`, so the stream refreshes it.
 */
export function useAlertPages(status: AlertStatus | undefined) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...alertKeys.all, 'pages', { status }] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => {
      const search = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (status) search.set('status', status);
      if (pageParam) search.set('before', pageParam);
      return api.request(`/api/v1/alerts?${search}`, { schema: AlertListResponseSchema, signal });
    },
    getNextPageParam: (last) =>
      last.alerts.length < PAGE_SIZE ? undefined : last.alerts.at(-1)?.openedAt,
  });
}

export function useAlertDetail(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...alertKeys.all, 'detail', id] as const,
    queryFn: ({ signal }) =>
      api.request(`/api/v1/alerts/${id}`, { schema: AlertDetailResponseSchema, signal }),
  });
}

export function useNoisiestRules(days = 7) {
  const api = useApi();
  return useQuery({
    queryKey: [...alertKeys.all, 'noisiest', days] as const,
    queryFn: ({ signal }) =>
      api.request(`/api/v1/alerts/noisiest?days=${days}`, {
        schema: NoisiestRulesResponseSchema,
        signal,
      }),
  });
}

function useAlertMutation<V, R>(send: (api: ReturnType<typeof useApi>, vars: V) => Promise<R>) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: V) => send(api, vars),
    // Settled, not success: a 409 means the alert changed under us, so re-read it too.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: alertKeys.all }),
  });
}

export const useAckAlert = () =>
  useAlertMutation((api, id: string) =>
    api.request(`/api/v1/alerts/${id}/ack`, { method: 'POST', schema: AlertSchema }),
  );

export const useSnoozeAlert = () =>
  useAlertMutation((api, { id, untilMinutes }: { id: string; untilMinutes: number }) =>
    api.request(`/api/v1/alerts/${id}/snooze`, {
      method: 'POST',
      body: { untilMinutes },
      schema: AlertSchema,
    }),
  );

export const useResolveAlert = () =>
  useAlertMutation((api, id: string) =>
    api.request(`/api/v1/alerts/${id}/resolve`, { method: 'POST', schema: AlertSchema }),
  );

/** Acknowledges every id, 200 per request; answers how many were acknowledged. */
export const useBulkAck = () =>
  useAlertMutation(async (api, ids: string[]) => {
    let acknowledged = 0;
    for (let i = 0; i < ids.length; i += BULK_MAX) {
      const res = await api.request('/api/v1/alerts/ack', {
        method: 'POST',
        body: { ids: ids.slice(i, i + BULK_MAX) },
        schema: BulkAckResponseSchema,
      });
      acknowledged += res.acknowledged;
    }
    return acknowledged;
  });
